import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PageHero } from "@/components/Section";
import Reveal from "@/components/Reveal";
import CTAButton from "@/components/CTAButton";
import { Field, TextInput, FormNotice } from "@/components/FormControls";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";

const GoogleButton = () => {
  const start = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      type="button"
      onClick={start}
      data-testid="google-signin-button"
      className="inline-flex min-h-[52px] w-full items-center justify-center gap-3 rounded-control bg-cream px-6 text-sm font-extrabold text-cocoa shadow-soft transition-shadow hover:shadow-softLg"
    >
      <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.9h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.4Z" />
        <path fill="#34A853" d="M12 22c2.7 0 4.9-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1a5.9 5.9 0 0 1-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22Z" />
        <path fill="#FBBC05" d="M6.4 14a6 6 0 0 1 0-3.9V7.5H3.1a10 10 0 0 0 0 9L6.4 14Z" />
        <path fill="#EA4335" d="M12 6c1.5 0 2.8.5 3.8 1.5l2.8-2.8A10 10 0 0 0 3.1 7.5l3.3 2.6A5.9 5.9 0 0 1 12 6Z" />
      </svg>
      Continue with Google
    </button>
  );
};

export default function SignInPage() {
  const brand = useBrand();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("signin");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [state, setState] = useState({ status: "idle", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      const path = mode === "signin" ? "/auth/login" : "/auth/register";
      const body =
        mode === "signin"
          ? { email: form.email, password: form.password }
          : { name: form.name, email: form.email, password: form.password, phone: form.phone };
      const { data } = await api.post(path, body);
      setUser(data);
      navigate("/account");
    } catch (err) {
      setState({
        status: "error",
        message: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    }
  };

  return (
    <>
      <PageHero 
        eyebrow="Customer account"
        title={mode === "signin" ? "Sign in to your account" : "Create your account"}
        intro={`Save your child's profile, dietary information and delivery details so ordering with ${brand.name} takes a moment rather than a form.`}
      />

      <section className="py-14 sm:py-20">
        <div className="mbm-container max-w-lg">
          <Reveal>
            <div className="mbm-card p-6 sm:p-8">
              <div className="flex w-full min-w-0 max-w-full gap-1.5 rounded-control bg-creamDeep p-1">
                {[
                  ["signin", "Sign in"],
                  ["signup", "Create account"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      setMode(id);
                      setState({ status: "idle", message: "" });
                    }}
                    data-testid={`auth-tab-${id}`}
                    className={`min-w-0 flex-1 rounded-[999px] min-h-[46px] px-2 text-center text-xs font-extrabold leading-tight transition-colors sm:text-sm ${
                      mode === id ? "bg-surface text-cocoa shadow-soft" : "text-cocoa/75"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <form onSubmit={submit} className="mt-6 grid gap-4" data-testid="auth-form">
                {mode === "signup" && (
                  <>
                    <Field label="Your name" required>
                      <TextInput required value={form.name} onChange={set("name")} data-testid="auth-name" />
                    </Field>
                    <Field label="Mobile number">
                      <TextInput type="tel" value={form.phone} onChange={set("phone")} data-testid="auth-phone" />
                    </Field>
                  </>
                )}
                <Field label="Email" required>
                  <TextInput required type="email" value={form.email} onChange={set("email")} data-testid="auth-email" />
                </Field>
                <Field
                  label="Password"
                  required
                  hint={mode === "signup" ? "At least 8 characters." : undefined}
                >
                  <TextInput
                    required
                    type="password"
                    minLength={8}
                    value={form.password}
                    onChange={set("password")}
                    data-testid="auth-password"
                  />
                </Field>

                {state.message && (
                  <FormNotice tone="error" testId="auth-error">
                    {state.message}
                  </FormNotice>
                )}

                <CTAButton
                  type="submit"
                  className="w-full"
                  disabled={state.status === "loading"}
                  data-testid="auth-submit"
                >
                  {state.status === "loading"
                    ? "Please wait…"
                    : mode === "signin"
                      ? "Sign in"
                      : "Create account"}
                </CTAButton>
              </form>

              <div className="my-6 flex items-center gap-3">
                <span className="mbm-rule flex-1" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cocoa/50">
                  or
                </span>
                <span className="mbm-rule flex-1" />
              </div>

              <GoogleButton />

              <p className="mt-6 text-center text-[13px] text-cocoa/70">
                Not ready to sign up?{" "}
                <Link to="/get-started" className="font-semibold text-coralInk hover:text-rust">
                  Start a plan instead
                </Link>
                .
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
